"use client";
import React, { useState, useRef, useEffect } from "react";
import Icon from "../icons/Icons";
import { formatCompactNumber } from "@/utils/commonFunctions";
import InformationButton from "./InformationButton";

interface CustomCalcFieldProps {
  label: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;

  modeOptions?: string[];
  defaultMode?: string;
  keyWord?: string | null;

  addedValuesList?: number[];
  onAddedValueClick?: (addedValue: number, newValue: number) => void;

  amountValue?: number;
  showFloatingValue?: boolean;
  unitPosition?: "left" | "right";
  onChange?: (value: number) => void;
  onModeChange?: (mode: string) => void;

  useRangeSlider?: boolean;
  useInputField?: boolean;
  allowDecimal?: boolean;

  amountMode?: string;
  showAmountModeToggle?: boolean; // whether to show % / ₹ toggle
  onAmountModeChange?: (mode: "₹" | "%") => void; // notify parent

  className?: string;
  isDarkMode?: boolean;
}

function formatIndianNumber(num: number): string {
  const [integerPart, decimalPart] = num.toString().split(".");

  const lastThree = integerPart.slice(-3);
  const otherNumbers = integerPart.slice(0, -3);

  let formattedInteger = integerPart;
  if (otherNumbers !== "") {
    formattedInteger = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  }

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

export default function CustomCalcField({
  label,
  min = 0,
  max = 0,
  step = 1,
  unit = "",

  modeOptions = [],
  defaultMode,
  keyWord,

  addedValuesList = [],
  onAddedValueClick,

  amountValue,
  showFloatingValue = false,
  unitPosition = "left",
  onChange,
  onModeChange,

  useRangeSlider = true,
  useInputField = true,
  allowDecimal = false,

  showAmountModeToggle = false,
  amountMode,
  onAmountModeChange,

  className,
  isDarkMode = false,
}: CustomCalcFieldProps) {
  const [value, setValue] = useState(amountValue ?? min);
  const [mode, setMode] = useState(defaultMode ?? modeOptions[0] ?? "");
  const [inputValue, setInputValue] = useState(formatIndianNumber(amountValue ?? min));
  const [error, setError] = useState(false);

  const rangeRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (amountValue == null) return;
    setValue(amountValue);
    setInputValue(formatIndianNumber(amountValue))
  }, [amountValue]);

  const [hasAdjusted, setHasAdjusted] = useState(false);

  const handleLabelClick = () => {
    if (inputRef.current && !hasAdjusted) {
      const input = inputRef.current;
      input.focus();
      const len = input.value.length;
      input.setSelectionRange(len, len);
      setHasAdjusted(true);
    }
  };

  const handleBlur = () => {
    setHasAdjusted(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/,/g, "");

    const regex = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
    if (!regex.test(raw)) return;

    if (raw === "") {
      raw = "0";
    }

    let num = allowDecimal ? parseFloat(raw) : Number(raw);

    const isPartialDecimal = allowDecimal && raw.endsWith(".");
    const isValidNumber = !isNaN(num);

    if (isValidNumber) {
      if (num > max) {
        num = max;
        raw = max.toString();
      }

      if (allowDecimal) num = Math.round(num * 100) / 100;

      setValue(num);
      if (min != 0) {
        setError(num === 0);
      }

      const formatted = isPartialDecimal ? formatIndianNumber(num) + "." : formatIndianNumber(num);

      setInputValue(formatted);
      onChange?.(num);
    } else {
      setInputValue(raw);
    }
  };

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = Number(e.target.value);
    setValue(num);
    setInputValue(formatIndianNumber(num));
    if (min != 0) {
      setError(num === 0 ? true : false);
    }
    onChange?.(num);
  };

  const handleModeChange = (opt: string) => {
    setMode(opt);
    onModeChange?.(opt);
  };

  const handleAmountModeChange = (mode: "₹" | "%") => {
    onAmountModeChange?.(mode);
  };

  const handleAddedValueClick = (addedValue: number) => {
    const newValue = Math.min(value + addedValue, max);

    setValue(newValue);
    setInputValue(formatIndianNumber(newValue));
    if (min != 0) {
      setError(newValue === 0);
    }

    onChange?.(newValue);
    onAddedValueClick?.(addedValue, newValue);
  };

  const percentage = max === min ? 0 : Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);

  const labelPositionClass =
    percentage < 25 ? "translate-x-0" : percentage > 75 ? "-translate-x-full" : "-translate-x-1/2";

  return (
    <div className={className ? className : "space-y-1 md:space-y-3"}>
      <div className="flex justify-between items-center gap-2">
        <div
          className={[
            "font-semibold text-left text-sm sm:text-base lg:text-xl",
            keyWord ? "flex items-center gap-1" : "",
            isDarkMode ? "text-white" : "text-primaryBlack",
          ].join(" ")}
        >
          {label}
          {keyWord && <InformationButton keywords={keyWord} className="w-4! h-4! sm:w-5! sm:h-5!" />}
        </div>

        {showAmountModeToggle && (
          <div className="flex items-center justify-between p-px sm:p-0.5 rounded-full border border-(--primary) overflow-hidden w-half-px h-6 sm:w-15 sm:h-7.5 ml-auto mr-1 xxxs:mr-0">
            <button
              onClick={() => handleAmountModeChange("%")}
              className={`w-5 h-5 sm:w-6.25 sm:h-6.25 transition duration-350 rounded-full flex items-center justify-center text-sm ${amountMode === "%" ? "bg-(--primary) text-white" : "text-(--primary) bg-white"}`}
            >
              %
            </button>
            <button
              onClick={() => handleAmountModeChange("₹")}
              className={`w-5 h-5 sm:w-6.25 sm:h-6.25 transition duration-350 rounded-full flex items-center justify-center text-sm ${amountMode === "₹" ? "bg-(--primary) text-white" : "text-(--primary) bg-white"}`}
            >
              ₹
            </button>
          </div>
        )}

        {useInputField && (
          <label
            onClick={handleLabelClick}
            className={[
              "max-w-30 xs:max-w-40 relative flex justify-start rounded-xl overflow-hidden py-2 px-3.5 font-medium text-xs sm:text-sm lg:text-base transition border",
              error
                ? "text-[#FF4D4D] border-[#FF4D4D] bg-[#ff4d4d0a]"
                : isDarkMode
                  ? "text-white border-[#cad3e2] bg-transparent focus-within:border-[#a0a4ac] hover:border-[#a0a4ac]"
                  : "text-(--primary) border-[rgb(var(--primary-alpha)/0.2)] bg-transparent focus-within:border-[rgb(var(--primary-alpha)/0.3)] hover:border-[rgb(var(--primary-alpha)/0.3)]",
            ].join(" ")}
          >
            {unitPosition == "left" && (
              <span
                className={[
                  "inline-block absolute top-1/2 -translate-y-1/2 my-auto opacity-50 pointer-events-none",
                  error ? "text-[#FF4D4D]" : isDarkMode ? "text-white" : "text-(--primary)",
                ].join(" ")}
              >
                {unit}
              </span>
            )}

            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onBlur={handleBlur}
              onChange={handleInputChange}
              className={["w-full bg-transparent", unitPosition === "right" ? "" : "pl-2.5 xs:pl-4"].join(" ")}
            />

            {unitPosition == "right" && (
              <span
                className={[
                  "inline-block absolute right-4 top-1/2 -translate-y-1/2 my-auto ml-auto opacity-50 pointer-events-none",
                  error ? "text-[#FF4D4D]" : isDarkMode ? "text-white" : "text-(--primary)",
                ].join(" ")}
              >
                {unit}
              </span>
            )}
          </label>
        )}
      </div>

      {modeOptions.length > 0 && (
        <div className="flex items-center gap-2 sm:gap-4">
          {modeOptions.map((opt) => (
            <button
              key={opt}
              onClick={() => handleModeChange(opt)}
              className={`px-3 xs:px-4 py-1 xs:py-1.5 text-xs xs:text-sm xs:leading-5 md:text-base md:leading-6 leading-4 w-full max-w-20 xs:max-w-24 md:max-w-32 text-center rounded-full border font-medium transition duration-350 ${mode === opt ? "bg-(--primary) text-white border-(--primary)" : "border-gray-400 text-gray-700 hover:bg-[#f4fffe] hover:border-(--primary)"}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {addedValuesList.length > 0 && (
        <div className="flex items-center gap-2 sm:gap-4">
          {addedValuesList.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => handleAddedValueClick(amount)}
              className="w-full max-w-24 py-2 text-xs sm:text-sm leading-1.3! font-medium text-[rgb(var(--primary-alpha)/0.5)] rounded-full border border-[rgb(var(--primary-alpha)/0.5)] transition hover:bg-[rgb(var(--primary-alpha)/0.05)] hover:border-(--primary) hover:text-(--primary)"
            >
              + {formatIndianNumber(amount)}
            </button>
          ))}
        </div>
      )}

      {/* Range slider */}
      {useRangeSlider && (
        <div className="relative h-10">
          {/* Track */}
          <div
            className={[
              "absolute left-0 top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full",
              isDarkMode ? "bg-[#474747]" : "bg-[#E8E8E8]",
            ].join(" ")}
          />

          {/* Filled Track */}
          <div
            className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full"
            style={{
              width: `${percentage}%`,
              background: "linear-gradient(90deg, #ACBFE6 0%, #00246B 100%)",
            }}
          />

          {/* Custom Thumb */}
          <div
            className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              left: `${percentage}%`,
            }}
          >
            <Icon.rangeBarCustomIcon className="w-7 h-7 sm:w-9 sm:h-9" />
          </div>

          {/* Real Range Input */}
          <input
            type="range"
            ref={rangeRef}
            min={min}
            max={max}
            step={step ?? 1}
            value={value}
            onChange={(e) => handleRangeChange(e)}
            className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
          />

          {showFloatingValue && (
            <div
              className={[
                "absolute -bottom-4.5 xs:-bottom-6 transition font-semibold text-xs sm:text-sm whitespace-nowrap",
                isDarkMode ? "text-white" : "text-primaryBlack",
                labelPositionClass,
              ].join(" ")}
              style={{ left: `calc(${percentage}%)` }}
            >
              {unit}
              {formatCompactNumber(value, false)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
